import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Copy, RotateCcw, Search, Trash2, Skull } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  deleteDeadLetterEvents,
  getDeadLetterEvents,
  requeueDeadLetterEvents,
  type DeadLetterRow,
} from "@/lib/dead-letter.functions";

export const Route = createFileRoute("/_authenticated/dead-letter")({
  component: DeadLetterPage,
  head: () => ({ meta: [{ title: "Dead-Letter Events — InboxPulse" }] }),
});

const EVENT_TYPES = ["open", "click", "bounce", "complaint", "block", "delayed", "sent"] as const;

const ANY = "__any__";

type EventType = "open" | "click" | "bounce" | "complaint" | "block" | "delayed" | "sent";

type FormState = {
  campaignId: string;
  eventType: EventType | undefined;
  errorContains: string;
  search: string;
  minAttempts: number;
  limit: number;
};

const EMPTY_FORM: FormState = {
  campaignId: "",
  eventType: undefined,
  errorContains: "",
  search: "",
  minAttempts: 0,
  limit: 100,
};

function DeadLetterPage() {
  const fetchDeadLetter = useServerFn(getDeadLetterEvents);
  const requeueFn = useServerFn(requeueDeadLetterEvents);
  const deleteFn = useServerFn(deleteDeadLetterEvents);
  const qc = useQueryClient();

  // Filter form state (committed on submit)
  const [filters, setFilters] = useState<FormState>(EMPTY_FORM);
  const [draft, setDraft] = useState<FormState>(EMPTY_FORM);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["dead-letter", filters],
    queryFn: () => fetchDeadLetter({ data: filters }),
    refetchInterval: 30_000,
  });

  const requeueMut = useMutation({
    mutationFn: (ids: string[]) => requeueFn({ data: { ids } }),
    onSuccess: (res) => {
      toast.success(`Requeued ${res.requeued} events`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["dead-letter"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Requeue failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (ids: string[]) => deleteFn({ data: { ids } }),
    onSuccess: (res) => {
      toast.success(`Deleted ${res.deleted} events`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["dead-letter"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  const rows = data?.rows ?? [];
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someSelected = selected.size > 0;

  const errorBuckets = useMemo(() => bucketErrors(rows), [rows]);

  const isAdminError = error instanceof Error && /forbidden|admin/i.test(error.message);

  const apply = () => {
    setFilters(draft);
    setSelected(new Set());
  };

  const reset = () => {
    setDraft(EMPTY_FORM);
    setFilters(EMPTY_FORM);
    setSelected(new Set());
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader
        title="Dead-letter events"
        description="Tracking queue rows that failed after max retries. Filter, requeue, or purge."
        actions={
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        }
      />

      <HelpPanel
        title="Hilfe: Liegen gebliebene Ereignisse aufräumen"
        what={
          <>
            Hier landen <b>Pixel-Treffer, die mehrfach fehlgeschlagen sind</b> — ein Fehler-Lager.
            Du kannst sie nochmal probieren (Requeue) oder löschen (Purge).
          </>
        }
        why={
          <>
            Solange Dead-letter wächst, geht Statistik verloren. Regelmäßig leeren / nochmal
            probieren.
          </>
        }
        how={[
          <>Filter setzen (z.B. nach Fehlertyp) — Übersicht behalten.</>,
          <>
            <b>Requeue</b>: zurück in die normale Warteschlange — wird nochmal verarbeitet.
          </>,
          <>
            <b>Purge</b>: endgültig löschen, wenn der Fehler bekannt und nicht reparierbar ist.
          </>,
        ]}
        tips={[
          <>
            Viele Einträge mit gleichem Fehler? Erst die Ursache beheben, dann Requeue — sonst
            landen sie wieder hier.
          </>,
        ]}
        glossary={[
          {
            term: "Dead-Letter",
            def: "Brief, der dauerhaft nicht zugestellt werden konnte. Hier: Ereignis, das nicht verarbeitet werden konnte.",
          },
          { term: "Requeue", def: "Zurück in die Warteschlange stellen — nochmal versuchen." },
          { term: "Purge", def: "Endgültig löschen." },
        ]}
      />

      <Card className="p-4 mb-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            apply();
          }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Campaign ID</label>
            <Input
              value={draft.campaignId ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, campaignId: e.target.value }))}
              placeholder="uuid"
              className="font-mono text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Event type</label>
            <Select
              value={draft.eventType ?? ANY}
              onValueChange={(v) =>
                setDraft((d) => ({
                  ...d,
                  eventType: v === ANY ? undefined : (v as EventType),
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any</SelectItem>
                {EVENT_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Error reason contains</label>
            <Input
              value={(draft.errorContains as string | undefined) ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, errorContains: e.target.value }))}
              placeholder="e.g. duplicate key"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              Free-text search (error / request_id / id)
            </label>
            <Input
              value={(draft.search as string | undefined) ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
              placeholder="search…"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Min retry attempts</label>
            <Input
              type="number"
              min={0}
              max={50}
              value={(draft.minAttempts as number | undefined) ?? 0}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  minAttempts: Number(e.target.value) || 0,
                }))
              }
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Limit</label>
            <Input
              type="number"
              min={1}
              max={500}
              value={(draft.limit as number | undefined) ?? 100}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  limit: Number(e.target.value) || 100,
                }))
              }
            />
          </div>

          <div className="md:col-span-2 lg:col-span-3 flex items-center gap-2 pt-2">
            <Button type="submit" size="sm">
              <Search className="size-4 mr-1" />
              Apply
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={reset}>
              Reset
            </Button>
            <div className="ml-auto text-xs text-muted-foreground">
              {data ? `${rows.length} shown${data.truncated ? ` of ${data.total}` : ""}` : ""}
            </div>
          </div>
        </form>
      </Card>

      {errorBuckets.length > 0 && (
        <Card className="p-4 mb-6">
          <div className="text-xs text-muted-foreground mb-2">
            Top failure reasons in current result set
          </div>
          <div className="flex flex-wrap gap-2">
            {errorBuckets.slice(0, 8).map((b) => (
              <button
                key={b.key}
                onClick={() => {
                  const next = { ...filters, errorContains: b.key };
                  setDraft(next);
                  setFilters(next);
                }}
                className="text-xs px-2 py-1 rounded-md border border-border bg-muted/40 hover:bg-accent transition font-mono"
                title={b.key}
              >
                <span className="text-destructive">{b.count}×</span>{" "}
                <span className="truncate max-w-[280px] inline-block align-bottom">{b.key}</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {isLoading && <Card className="p-6 text-muted-foreground">Loading dead-letter events…</Card>}

      {error && (
        <Card className="p-6 border-destructive/50 bg-destructive/5 text-destructive flex items-start gap-3">
          <AlertTriangle className="size-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">
              {isAdminError ? "Admin access required" : "Failed to load dead-letter events"}
            </div>
            <div className="text-sm opacity-80 mt-1">
              {error instanceof Error ? error.message : String(error)}
            </div>
          </div>
        </Card>
      )}

      {!isLoading && !error && (
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-border/60 flex items-center gap-3 flex-wrap">
            <Skull className="size-4 text-destructive" />
            <h2 className="text-sm font-medium">{rows.length} dead-letter rows</h2>
            <div className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!someSelected || requeueMut.isPending}
                onClick={() => requeueMut.mutate(Array.from(selected))}
              >
                <RotateCcw className="size-4 mr-1" />
                Requeue ({selected.size})
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={!someSelected || deleteMut.isPending}
                onClick={() => {
                  if (
                    confirm(
                      `Permanently delete ${selected.size} dead-letter rows? This cannot be undone.`,
                    )
                  ) {
                    deleteMut.mutate(Array.from(selected));
                  }
                }}
              >
                <Trash2 className="size-4 mr-1" />
                Delete ({selected.size})
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={(c) => {
                        if (c) setSelected(new Set(rows.map((r) => r.id)));
                        else setSelected(new Set());
                      }}
                    />
                  </th>
                  <th className="text-left px-3 py-2 font-medium">Campaign</th>
                  <th className="text-left px-3 py-2 font-medium">Event</th>
                  <th className="text-left px-3 py-2 font-medium">Provider</th>
                  <th className="text-right px-3 py-2 font-medium">Attempts</th>
                  <th className="text-left px-3 py-2 font-medium">Last error</th>
                  <th className="text-left px-3 py-2 font-medium">Request</th>
                  <th className="text-right px-3 py-2 font-medium">Enqueued</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                      No dead-letter events match the current filters.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <DeadLetterRowView
                    key={r.id}
                    row={r}
                    selected={selected.has(r.id)}
                    onToggle={(checked) =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(r.id);
                        else next.delete(r.id);
                        return next;
                      })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function DeadLetterRowView({
  row,
  selected,
  onToggle,
}: {
  row: DeadLetterRow;
  selected: boolean;
  onToggle: (checked: boolean) => void;
}) {
  return (
    <tr className={cn("border-t border-border/50", selected && "bg-accent/40")}>
      <td className="px-3 py-2 align-top">
        <Checkbox checked={selected} onCheckedChange={(c) => onToggle(Boolean(c))} />
      </td>
      <td className="px-3 py-2 align-top">
        <div className="font-medium truncate max-w-[200px]">{row.campaign_name ?? "(unnamed)"}</div>
        <CopyId id={row.campaign_id} />
      </td>
      <td className="px-3 py-2 align-top">
        <span className="text-xs px-2 py-0.5 rounded bg-muted font-mono">{row.event_type}</span>
      </td>
      <td className="px-3 py-2 align-top text-muted-foreground">{row.provider ?? "—"}</td>
      <td className="px-3 py-2 align-top text-right tabular-nums font-medium text-destructive">
        {row.attempts}
      </td>
      <td className="px-3 py-2 align-top">
        <div
          className="text-xs text-destructive font-mono max-w-[420px] line-clamp-3"
          title={row.last_error ?? ""}
        >
          {row.last_error ?? "—"}
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        {row.request_id ? (
          <CopyId id={row.request_id} />
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        )}
      </td>
      <td className="px-3 py-2 align-top text-right text-xs text-muted-foreground">
        {new Date(row.enqueued_at).toLocaleString()}
      </td>
    </tr>
  );
}

function CopyId({ id }: { id: string }) {
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(id);
        toast.success("Copied");
      }}
      className="text-[10px] font-mono text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
      title={id}
    >
      <span className="truncate max-w-[180px]">{id}</span>
      <Copy className="size-3" />
    </button>
  );
}

function bucketErrors(rows: DeadLetterRow[]): Array<{ key: string; count: number }> {
  const counts = new Map<string, number>();
  for (const r of rows) {
    if (!r.last_error) continue;
    // Normalize to first sentence / 80 chars so similar errors bucket together.
    const key = r.last_error.split("\n")[0].slice(0, 80);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count);
}
